import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save } from 'lucide-react';

export default function SubscriptionEditHeader({ 
  onSave, 
  onCancel,
  saving, 
  hasUnsavedChanges, 
  title = "editSubscription",
  saveButtonText = null // Allow custom save button text
}) {
  const { t } = useTranslation();

  // Determine save button text based on the title if not explicitly provided
  const getSaveButtonText = () => {
    if (saveButtonText) return saveButtonText;
    return title === "createSubscription" ? t("create") : t("save");
  };

  return (
    <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <div className="flex items-center">
            <button
              onClick={onCancel}
              className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {t("backToSubscriptions")}
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t(title)}
              </h1>
              {hasUnsavedChanges && (
                <p className="text-sm text-amber-600 font-medium mt-1">
                  {t('unsavedChanges')}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              {t("cancel")}
            </button>
            
            <button 
              onClick={onSave}
              disabled={saving || !hasUnsavedChanges}
              className={`
                inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-lg
                shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                transition-all duration-200
                ${saving 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : hasUnsavedChanges
                    ? 'bg-blue-600 text-white hover:bg-blue-700 active:scale-95'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }
              `}
            >
              {saving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('saving')}
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {getSaveButtonText()}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
